import React, { Component } from "react";
import { connect } from "react-redux";
import has from "lodash/has";
import PropTypes from "prop-types";
import uniqid from "uuid/v4";
import Layout from "layouts/DashboardManage";
import { getClient, updateClient } from "store/actions/clientAction";
import {
  getDepartment,
  getAllDepartments
} from "store/actions/departmentAction";
import { getWorkflow, getAllWorkflows } from "store/actions/workflowAction";
import { getAllMetadata } from "store/actions/metadataAction";
import { getAllUsers } from "store/actions/userAction";
import SubmitButton from "components/Forms/Buttons/Submit";
import AutoSuggest from "components/Forms/Elements/AutoSuggest";
import Textbox from "components/Forms/Elements/Textbox";
import ErrorLabel from "components/Forms/Elements/ErrorLabel";
import { SpinnerSyncBlock } from "components/Spinner";
import DualMultiSelect from "components/Forms/Elements/DualMultiSelect";
import Notification, { notifyApiError } from "components/Notification";
import Accuracy from "./Accuracy";
import Adapters from "./Adapters";
import Translate from "helpers/Translate";

export class Edit extends Component {
  state = {
    name: "",
    department: "",
    departmentName: "",
    workflow: "",
    adapter: {},
    users: [],
    metadata: [],
    accuracy_conditions: [],
    rate_per_hundred_characters: ""
  };

  componentDidMount() {
    this.props.getClient(this.props.match.params.clientId);
    this.props.getAllDepartments({});
    this.props.getAllUsers({});
    this.props.getAllWorkflows({ clientId: this.props.match.params.clientId });
    this.props.getAllMetadata({});
  }

  componentDidUpdate(prevProps) {
    // Check if the data has loaded
    if (
      prevProps.client !== this.props.client &&
      has(this, "props.client.data")
    ) {
      const clientData = { ...this.props.client.data };
      if (clientData?.users?.length) {
        clientData.users = clientData.users.map(user => ({
          label: user.fullname,
          value: user.id
        }));
      }
      if (clientData?.metadata?.length) {
        clientData.metadata = clientData.metadata.map(md => ({
          label: md.title,
          value: md.id
        }));
      }

      this.setState(clientData, () => {
        this.props.getDepartment(this.state.department.id);
        this.props.getWorkflow(this.state.workflow.id);
      });
    }

    // When the new client is updated successfully, we will redirect manage page
    if (
      prevProps.updateClientResponse !== this.props.updateClientResponse &&
      has(this, "props.updateClientResponse.id")
    ) {
      return this.props.history.push(`/manage/clients`);
    }

    // When there was a problem updating the client
    if (
      prevProps.errorClient !== this.props.errorClient &&
      this.props.errorClient &&
      Object.keys(this.props.errorClient).length > 0 &&
      has(this, "props.errorClient.response.data.data.errors")
    ) {
      let hasField = false;
      for (const error of this.props.errorClient.response.data.data.errors) {
        if (error.field === "fields") {
          hasField = true;
        }
      }
      if (hasField) {
        notifyApiError(this.props.errorClient);
      } else {
        Notification.error(
          Translate({ id: "an_error_occurred_submitting_the_form" })
        );
      }
    }
  }

  handleChange = e => {
    this.setState({
      [e.target.name]: e.target.value
    });
  };

  handleAddNewCondition = () => {
    this.setState({
      accuracy_conditions: [
        ...this.state.accuracy_conditions,
        {
          id: uniqid(),
          uuid: uniqid(),
          accuracy_from: {
            operator: "",
            value: ""
          },
          accuracy_to: {
            operator: "",
            value: ""
          },
          qc_value: ""
        }
      ]
    });
  };

  handleAccuracy = ({ e, id }) => {
    const target = e.target || {};

    this.setState(prevState => {
      const accuracy = prevState.accuracy_conditions.map(item => {
        if (item.id === id) {
          return {
            ...item,
            accuracy_from: {
              operator:
                target.name === "accuracy_from" && target.id === "operator"
                  ? target.value
                  : item.accuracy_from.operator,
              value:
                target.name === "accuracy_from" && target.id === "value"
                  ? target.value
                  : item.accuracy_from.value
            },
            accuracy_to: {
              operator:
                target.name === "accuracy_to" && target.id === "operator"
                  ? target.value
                  : item.accuracy_to.operator,
              value:
                target.name === "accuracy_to" && target.id === "value"
                  ? target.value
                  : item.accuracy_to.value
            },
            qc_value: target.name === "qc_value" ? target.value : item.qc_value
          };
        }

        return item;
      });

      return {
        accuracy_conditions: accuracy
      };
    });
  };

  handleUpdate = () => {
    this.props.updateClient({
      id: this.props.match.params.clientId,
      data: {
        ...this.state,
        department: this.state.department?.id
          ? this.state.department.id
          : this.state.department,
        users: this.state.users.map(u => u.value),
        metadata: this.state.metadata.map(md => md.value),
        workflow: this.state.workflow?.id
          ? this.state.workflow.id
          : this.state.workflow,
        rate_per_hundred_characters: this.state.rate_per_hundred_characters
      }
    });
  };

  render() {
    return (
      <Layout>
        {this.props.isLoading && <SpinnerSyncBlock />}
        <div className="add-edit-client">
          <h1 className="title mb-30">
            {Translate({ id: "add_edit_client" })}
          </h1>

          <div className="tabs">
            <div className="tab tab-manage">
              {Translate({ id: "step_1_manage_client" })}
            </div>
            <div className="tab tab-design selected">
              {Translate({ id: "step_2_update_client_details" })}
            </div>
          </div>

          <div className="tab-wrapper">
            <div className="tab-design-wrapper selected">
              <div className="form-wrapper">
                <div className="form mt-50">
                  {/* <!-- Left Column --> */}
                  <div className="left">
                    <div className="form-group mb-20">
                      <label htmlFor="name">
                        * {Translate({ id: "client_name" })}
                      </label>
                      <Textbox
                        type="text"
                        placeholder="Acme ltd"
                        id="clientname"
                        name="name"
                        fieldValue={this.state.name}
                        onChange={this.handleChange}
                        onReturnKey={this.handleUpdate}
                        errors={this.props.errorClient}
                        errorKey="name"
                        formErrorClass="form-error"
                      />
                    </div>
                    <div className="form-group mb-20">
                      <label htmlFor="name">
                        * {Translate({ id: "adapter" })}
                      </label>
                      <Adapters
                        fieldValues={this.state.adapter}
                        clientData={this?.props?.client?.data}
                        onChange={e => {
                          this.setState({
                            adapter: {
                              ...this.state.adapter,
                              [e.target.name]: e.target.value
                            }
                          });
                        }}
                        errors={this.props.errorClient}
                      />
                    </div>
                    <div className="form-group mb-20">
                      <label htmlFor="collection">
                        * {Translate({ id: "select_collection" })}
                        <span
                          data-tip={Translate({
                            id: "collections_are_essentially_departments"
                          })}
                          style={{ float: "right" }}
                        >
                          <i className="far fa-question-circle"></i>
                        </span>
                      </label>
                      <AutoSuggest
                        id="collectionlist"
                        fieldValue={
                          has(this, "state.department.name")
                            ? this.state.department.name
                            : this.state?.departmentName || ""
                        }
                        data={
                          has(this, "props.departments.data")
                            ? this.props.departments.data
                            : []
                        }
                        keyword={value => {
                          this.props.getAllDepartments({ name: value });
                          this.setState({ department: "" });
                        }}
                        displayItemInList="name"
                        onSelect={selectedItem => {
                          this.setState({
                            department: selectedItem.id,
                            departmentName: selectedItem.name
                          });
                        }}
                        errors={this.props.errorClient}
                        errorKey="department"
                      />
                    </div>
                    <div className="form-group mb-20">
                      <label htmlFor="workflow">
                        * {Translate({ id: "select_workflow" })}
                      </label>
                      <AutoSuggest
                        id="workflowlist"
                        fieldValue={
                          has(this, "props.workflow.data.name")
                            ? this.props.workflow.data.name
                            : ""
                        }
                        data={
                          has(this, "props.workflows.data")
                            ? this.props.workflows.data
                            : []
                        }
                        keyword={value => {
                          this.props.getAllWorkflows({
                            clientId: this.props.match.params.clientId,
                            name: value
                          });
                          this.setState({ workflow: "" });
                        }}
                        displayItemInList="name"
                        onSelect={selectedItem =>
                          this.setState({ workflow: selectedItem.id })
                        }
                        errors={this.props.errorClient}
                        errorKey="workflow"
                      />
                    </div>

                    <div className="form-group mb-20">
                      <label htmlFor="name">
                        * {Translate({ id: "rate_per_hundred_characters" })}
                      </label>
                      <Textbox
                        type="text"
                        placeholder="0"
                        id="rate_per_hundred_characters"
                        name="rate_per_hundred_characters"
                        fieldValue={this.state.rate_per_hundred_characters}
                        onChange={this.handleChange}
                        onReturnKey={this.handleUpdate}
                        errors={this.props.errorClient}
                        errorKey="rate_per_hundred_characters"
                        formErrorClass="form-error"
                      />
                    </div>

                    {/* Users */}
                    <div className="form-group mb-20">
                      <div className="form-input-vgroup metadata1">
                        <DualMultiSelect
                          selectItemsLabel={`* ${Translate({
                            id: "select_users"
                          })}`}
                          selectedItemsLabel={Translate({
                            id: "selected_users"
                          })}
                          placeholder={Translate({ id: "search_user" })}
                          items={
                            has(this, "props.users.data")
                              ? this.props.users.data.map(item => ({
                                  label: item.fullname,
                                  value: item.id
                                }))
                              : []
                          }
                          selectedItems={this.state.users}
                          onSearch={e => {
                            const target = e.target;
                            this.props.getAllUsers({ name: target.value });
                          }}
                          onSearchReturnKey={() => {}}
                          onRemoveItem={userId => {
                            this.setState(prev => {
                              return {
                                users: prev.users.filter(
                                  u => u.value !== userId
                                )
                              };
                            });
                          }}
                          onSelect={item => {
                            this.setState({
                              users: [...this.state.users, item]
                            });
                          }}
                          errors={{}}
                          errorKey="users"
                        />
                      </div>
                      <ErrorLabel
                        errorKey="users"
                        errors={this.props.errorClient}
                      />
                    </div>

                    {/* Metadata */}
                    <div className="form-group mb-20">
                      <div className="form-input-vgroup metadata1">
                        <DualMultiSelect
                          selectItemsLabel={`* ${Translate({
                            id: "select_metadata"
                          })}`}
                          selectedItemsLabel={Translate({
                            id: "selected_metadata"
                          })}
                          placeholder={Translate({
                            id: "search_metadata_title"
                          })}
                          items={
                            has(this, "props.metadata.data")
                              ? this.props.metadata.data.map(item => ({
                                  label: item.title,
                                  value: item.id
                                }))
                              : []
                          }
                          selectedItems={this.state.metadata}
                          onSearch={e => {
                            const target = e.target;
                            this.props.getAllMetadata({ title: target.value });
                          }}
                          onSearchReturnKey={() => {}}
                          onRemoveItem={metadataId => {
                            this.setState(prev => {
                              return {
                                metadata: prev.metadata.filter(
                                  m => m.value !== metadataId
                                )
                              };
                            });
                          }}
                          onSelect={item => {
                            this.setState({
                              metadata: [...this.state.metadata, item]
                            });
                          }}
                          errors={{}}
                          errorKey="metadata"
                        />
                      </div>
                      <ErrorLabel
                        errorKey="metadata"
                        errors={this.props.errorClient}
                      />
                    </div>
                  </div>
                  {/* <!-- Right Column --> */}
                  <div className="right">
                    <h2 className="title">
                      * {Translate({ id: "accuracy_and_quality_check" })}
                    </h2>
                    <div className="help">
                      (
                      {Translate({
                        id: "the_first_matching_condition_will_be_affected"
                      })}
                      )
                    </div>

                    {/* <!-- Conditions --> */}
                    {this.state.accuracy_conditions.map((item, idx) => {
                      return (
                        <Accuracy
                          key={idx}
                          item={item}
                          fieldValue={
                            this.state.accuracy_conditions.length > 0 &&
                            this.state.accuracy_conditions.find(
                              a => a.id === item.id
                            )
                          }
                          onChange={e =>
                            this.handleAccuracy({
                              e,
                              id: item.id
                            })
                          }
                          onRemove={() => {
                            this.setState(prevState => {
                              return {
                                accuracy_conditions:
                                  prevState.accuracy_conditions.filter(
                                    c => c.id !== item.id
                                  )
                              };
                            });
                          }}
                          onReturnKey={this.handleUpdate}
                          errors={this.props.errorClient}
                        />
                      );
                    })}
                    <button
                      data-test-btn="handleAddNewCondition"
                      className="btn btn-default btn-full mt-20"
                      onClick={this.handleAddNewCondition}
                    >
                      <i className="fas fa-plus mr-5"></i>
                      {Translate({ id: "add_condition" })}
                    </button>
                    {has(this, "props.errorClient.response.data.data.errors") &&
                      this.props.errorClient.response.data.data.errors.find(
                        e =>
                          e.field === "accuracy_conditions" &&
                          e.validation === "min"
                      ) && (
                        <ErrorLabel
                          errorKey="accuracy_conditions"
                          errors={this.props.errorClient}
                          formErrorClass="mt-10"
                        />
                      )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* <!-- Save/Cancel Buttons --> */}
          <div className="save-cancel-buttons">
            {/* <!-- Cancel Button --> */}
            <button
              onClick={() => this.props.history.push(`/manage/clients`)}
              className="btn manage-btn-cancel"
            >
              <i className="far fa-times-circle mr-10"></i> {Translate({ id: "cancel" })}
            </button>

            {/* <!-- Save Button --> */}
            <SubmitButton
              className="btn btn-primary manage-btn-save"
              buttonIconClass="fas fa-save mr-10"
              buttonText={Translate({ id: "save_changes" })}
              btnId="create"
              isLoading={this.props.isLoading}
              handleSubmit={this.handleUpdate}
            />
          </div>
        </div>
      </Layout>
    );
  }
}

Edit.propTypes = {
  client: PropTypes.object,
  departments: PropTypes.object,
  workflows: PropTypes.object,
  metadata: PropTypes.object,
  getClientResponse: PropTypes.func,
  updateClientResponse: PropTypes.object,
  errorClient: PropTypes.object,
  isLoading: PropTypes.bool,
  getClient: PropTypes.func,
  updateClient: PropTypes.func,
  getAllDepartments: PropTypes.func
};

const mapStateToProps = state => {
  return {
    client: state.client.getClient,
    department: state.department.getDepartment,
    departments: state.department.getAllDepartments,
    workflow: state.workflow.getWorkflow,
    workflows: state.workflow.getAllWorkflows,
    users: state.user.getAllUsers,
    metadata: state.metadata.getAllMetadata,
    updateClientResponse: state.client.updateClient,
    errorClient: state.client.errorClient,
    isLoading: state.client.isLoadingClient
  };
};

export default connect(mapStateToProps, {
  getClient,
  updateClient,
  getDepartment: getDepartment,
  getAllDepartments: getAllDepartments,
  getWorkflow,
  getAllUsers,
  getAllWorkflows,
  getAllMetadata
})(Edit);
